import * as styles from "./ListItem.css";

import { clsx } from "clsx";
import type { JSX, KeyboardEvent, MouseEvent } from "react";

import { useListContext } from "./List.context";
import type { ListItemProps } from "./List.types";
import {
  useListVirtualItem,
  virtualItemStyle,
} from "./ListVirtualItem.context";

export function ListItem({
  value,
  selected,
  disabled = false,
  onSelect,
  startSection,
  endSection,
  className,
  style,
  children,
  onClick,
  onKeyDown,
  ...props
}: ListItemProps): JSX.Element {
  const { size, separators, selectable, selectedValue, selectValue } =
    useListContext();

  const virtualItem = useListVirtualItem();

  const isSelected =
    selected ?? (undefined !== value && value === selectedValue);

  const interactive =
    !disabled &&
    (selectable || undefined !== onSelect || undefined !== onClick);

  function activate() {
    if (disabled || undefined === value) {
      return;
    }

    onSelect?.(value);
    selectValue(value);
  }

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    onClick?.(event);

    if (!event.defaultPrevented) {
      activate();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);

    if ("Enter" === event.key || " " === event.key) {
      event.preventDefault();
      activate();
    }
  }

  return (
    <div
      data-slot="list-item"
      data-index={virtualItem?.index}
      data-selected={isSelected || undefined}
      data-disabled={disabled || undefined}
      ref={virtualItem?.measure}
      role={selectable ? "option" : undefined}
      aria-selected={selectable ? isSelected : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? undefined : isSelected ? 0 : -1}
      className={clsx(
        styles.item({ size, interactive, separated: separators }),
        className,
      )}
      style={{ ...virtualItemStyle(virtualItem), ...style }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {undefined !== startSection && (
        <span className={styles.section}>{startSection}</span>
      )}

      <span className={styles.content}>{children}</span>

      {undefined !== endSection && (
        <span className={styles.section}>{endSection}</span>
      )}
    </div>
  );
}
